"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useConsent } from "@/lib/consent-context";
import { storyblokImage } from "@/lib/storyblok-image-loader";
import { posterFallback, youtubeThumbnail } from "@/lib/youtube";
import { VideoConsentGate } from "@/components/video/video-consent-gate";

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

type PlayerState = "playing" | "paused" | "ended";

type Props = {
  videoId: string;
  title?: string;
  posterSrc?: string;
  className?: string;
  inline?: boolean;
  autoStart?: boolean;
  ratio?: string;
  autoFocus?: boolean;
  onStateChange?: (state: PlayerState) => void;
};

function isCoarsePointer() {
  return typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
}

function isAndroid() {
  return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 68 48" className={className} aria-hidden>
      <path
        fill="#ff0000"
        d="M66.5 7.5c-.8-3-3.2-5.4-6.2-6.2C54.9 0 34 0 34 0S13.1 0 7.7 1.3C4.7 2.1 2.3 4.5 1.5 7.5 0 13 0 24 0 24s0 11 1.5 16.5c.8 3 3.2 5.4 6.2 6.2C13.1 48 34 48 34 48s20.9 0 26.3-1.3c3-.8 5.4-3.2 6.2-6.2C68 35 68 24 68 24s0-11-1.5-16.5z"
      />
      <path fill="#fff" d="M27 34V14l17 10z" />
    </svg>
  );
}

export default function YouTubePlayer({
  videoId,
  title,
  posterSrc,
  className,
  inline = true,
  autoStart = false,
  ratio,
  autoFocus = true,
  onStateChange,
}: Props) {
  const { consent } = useConsent();
  const canEmbed = consent.marketing;
  const nativeMobile = !autoStart && isCoarsePointer();
  const playInline = inline && !nativeMobile;
  const [playing, setPlaying] = useState(autoStart);
  const [ready, setReady] = useState(false);
  const outerRef = useRef<HTMLDivElement>(null);
  const embedRef = useRef<HTMLDivElement>(null);
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  useIsomorphicLayoutEffect(() => {
    if (!playing || !canEmbed || !embedRef.current) return;
    let cancelled = false;
    let player: { destroy: () => void } | null = null;
    let isReady = false;
    let destroyRequested = false;
    let captionRetry: ReturnType<typeof setTimeout> | null = null;

    const teardown = () => {
      try {
        player?.destroy();
      } catch {}
      player = null;
    };

    (async () => {
      const [PlyrMod] = await Promise.all([import("plyr"), import("./plyr.css")]);
      if (cancelled || !embedRef.current) return;
      const Plyr = PlyrMod.default;
      const instance = new Plyr(embedRef.current, {
        autoplay: true,
        ratio,
        keyboard: { focused: true, global: true },
        controls: [
          "play-large",
          "play",
          "progress",
          "current-time",
          "mute",
          "volume",
          "captions",
          "settings",
          "pip",
          "airplay",
          "fullscreen",
        ],
        settings: ["captions", "quality", "speed"],
        fullscreen: { enabled: true, iosNative: true },
        youtube: {
          noCookie: true,
          rel: 0,
          modestbranding: 1,
          playsinline: playInline ? 1 : 0,
        },
      });
      const stripIframeTab = () => {
        const iframe = outerRef.current?.querySelector("iframe");
        if (iframe) iframe.tabIndex = -1;
      };
      const clearCaptionTrack = () => {
        const yt = instance.embed as
          { setOption?: (module: string, option: string, value: unknown) => void } | undefined;
        try {
          yt?.setOption?.("captions", "track", {});
          yt?.setOption?.("cc", "track", {});
        } catch {}
      };

      const handleReady = () => {
        isReady = true;
        if (destroyRequested) {
          teardown();
          return;
        }
        stripIframeTab();
        clearCaptionTrack();
        setReady(true);
        if (autoFocus) {
          const container = outerRef.current?.querySelector<HTMLElement>(".plyr");
          if (container) {
            container.tabIndex = 0;
            container.focus();
          }
        }
      };
      instance.on("ready", handleReady);
      stripIframeTab();
      instance.on("playing", () => {
        onStateChangeRef.current?.("playing");
        clearCaptionTrack();
        if (captionRetry) clearTimeout(captionRetry);
        captionRetry = setTimeout(clearCaptionTrack, 1500);
        if (nativeMobile && isAndroid()) {
          try {
            instance.fullscreen.enter();
          } catch {}
        }
      });
      instance.on("pause", () => onStateChangeRef.current?.("paused"));
      instance.on("ended", () => onStateChangeRef.current?.("ended"));
      player = instance;
    })();
    return () => {
      cancelled = true;
      if (captionRetry) clearTimeout(captionRetry);
      if (isReady) teardown();
      else destroyRequested = true;
    };
  }, [playing, canEmbed, videoId, playInline, nativeMobile, ratio, autoFocus]);

  const poster = storyblokImage(posterSrc || youtubeThumbnail(videoId), 1280);
  const posterSet = posterSrc
    ? [640, 960, 1280].map((w) => `${storyblokImage(posterSrc, w)} ${w}w`).join(", ")
    : undefined;

  if (!playing) {
    return (
      <button
        type="button"
        onClick={() => setPlaying(true)}
        aria-label={title ? `Play ${title}` : "Play video"}
        className={`video-poster-btn group relative block size-full cursor-pointer overflow-hidden ${className || ""}`}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={posterSrc ? storyblokImage(posterSrc, 750) : poster}
          srcSet={posterSet}
          sizes={posterSet ? "(min-width: 1280px) 1280px, 100vw" : undefined}
          width={1280}
          height={720}
          alt={title ? `${title} video` : "Video"}
          aria-hidden
          className="absolute inset-0 size-full object-cover"
          loading="lazy"
          onError={posterFallback}
        />
        <span className="absolute inset-0 bg-black/10 transition-colors group-hover:bg-black/0" />
        <span className="absolute inset-0 flex items-center justify-center">
          <PlayIcon className="h-[36px] w-[54px] transition-transform group-hover:scale-110 lg:h-15 lg:w-[93px]" />
        </span>
      </button>
    );
  }

  if (!canEmbed) {
    return <VideoConsentGate poster={poster} className={className} />;
  }

  return (
    <div
      ref={outerRef}
      data-plyr-ratio={ratio || "16:9"}
      className={`relative size-full bg-black ${className || ""}`}
    >
      <div
        className="plyr__video-embed size-full transition-opacity duration-150"
        ref={embedRef}
        style={{ opacity: ready ? 1 : 0 }}
      >
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1${playInline ? "&playsinline=1" : ""}`}
          title={title || "Video"}
          allowFullScreen
          allow="autoplay; encrypted-media; picture-in-picture; web-share"
          tabIndex={-1}
        />
      </div>
    </div>
  );
}
